import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isAuthenticated, redirectToLogin } from '../../utils/auth';
import Spinner from '../common/Spinner';

interface Props {
  children: ReactNode;
  requiredRoles?: Array<'admin' | 'billing' | 'tech' | 'sa'>;
}

export default function ProtectedRoute({ children, requiredRoles }: Props) {
  const { isLoading, user } = useAuth();

  useEffect(() => {
    if (!isAuthenticated()) {
      redirectToLogin();
    }
  }, []);

  if (!isAuthenticated()) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    redirectToLogin();
    return null;
  }

  // Check role-based access
  if (requiredRoles && user.role) {
    if (!requiredRoles.includes(user.role)) {
      // User doesn't have required role - redirect to services
      return <Navigate to="/services" replace />;
    }
  }

  return <>{children}</>;
}
